import { Button, Image, Input, Spinner } from "@fluentui/react-components";
import React, { useEffect, useState } from "react";
import { I18nextProvider, useTranslation } from 'react-i18next';
import i18next from "../../config";
import { CaptchaChallengeType, CaptchaControlProps, GetCaptchaChallengeResponse } from "./Captcha.types";
import './captcha.scss';
import { getCaptcha, verifyCaptcha } from "./captchaApi";

function CaptchaControlInternal({ onVerification, onCaptchaLoad, baseUrl }: CaptchaControlProps) {
    const { t, i18n } = useTranslation();
    const [challengeType, setChallengeType] = useState<CaptchaChallengeType>(CaptchaChallengeType.Visual);
    const [challengeTypeLabel, setChallengeTypeLabel] = useState<string>(t("HIP.visualHelper"));
    const [getCaptchaResponse, setGetCaptchaResponse] = useState<GetCaptchaChallengeResponse | undefined>();
    const [inputSolution, setInputSolution] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isVerifying, setIsVerifying] = useState<boolean>(false);

    useEffect(() => {
        let ignore = false;
        setIsLoading(true);
        getCaptcha({
            challengeType: challengeType,
            locale: i18n.language
        }, baseUrl).then(response => {
            if (!ignore) {
                setIsLoading(false);
                setGetCaptchaResponse(response);
                if (onCaptchaLoad) {
                    onCaptchaLoad(response);
                }
            }
        });

        if (inputSolution !== '') {
            setInputSolution('');
        }

        return () => {
            ignore = true;
        }
    }, [challengeType]);

    const handleChallengeTypeChange = (challengeType: CaptchaChallengeType) => {
        if (challengeType == CaptchaChallengeType.Visual) {
            setChallengeType(CaptchaChallengeType.Visual);
            setChallengeTypeLabel(t("HIP.visualHelper"));
        }
        else {
            setChallengeType(CaptchaChallengeType.Audio);
            setChallengeTypeLabel(t("HIP.audioHelper"));
        }
    };

    async function handleNewChallengeRequest() {
        setIsLoading(true);
        const response = await getCaptcha({
            challengeType: challengeType,
            locale: i18n.language
        }, baseUrl);
        setIsLoading(false);
        setGetCaptchaResponse(response);

        if (onCaptchaLoad) {
            onCaptchaLoad(response);
        }

        setInputSolution('');
    };

    async function handleVerification() {
        if (getCaptchaResponse) {
            setIsVerifying(true);
            verifyCaptcha({
                challengeId: getCaptchaResponse.challengeId,
                solution: inputSolution,
                azureRegion: getCaptchaResponse.azureRegion,
                challengeType: challengeType,
                correlationId: getCaptchaResponse.correlationId,
                clientRequestId: getCaptchaResponse.clientRequestId,
            }, baseUrl)
                .then(response => {
                    setIsVerifying(false);
                    if (!response?.solved) {
                        handleNewChallengeRequest();
                    }
                    onVerification(response)
                })
                .catch(error => {
                    setIsVerifying(false);
                    onVerification(error);
                });
        }
    };

    return (
        <div className="captcha-container">
            <p id="captchaTitle">{t("HIP.title")}</p>
            <div className="captcha-container__linkbox">
                <Button
                    data-testid="captchaNewLink"
                    id="captchaNewLink"
                    onClick={handleNewChallengeRequest}>
                    {t("HIP.new")}
                </Button> <span aria-hidden> | </span>
                {challengeType === CaptchaChallengeType.Visual && getCaptchaResponse ?
                    <Button
                        data-testid="captchaAudioLink"
                        id="captchaAudioLink"
                        onClick={() => handleChallengeTypeChange(CaptchaChallengeType.Audio)}>
                        {t("HIP.audio")}
                    </Button> :
                    <Button
                        data-testid="captchaVisualLink"
                        id="captchaVisualLink"
                        onClick={() => handleChallengeTypeChange(CaptchaChallengeType.Visual)}>
                        {t("HIP.visual")}
                    </Button>
                }
            </div>
            <div className="captcha-content" aria-live="assertive" aria-atomic="true">
                {challengeType === CaptchaChallengeType.Visual && getCaptchaResponse && !isLoading ?
                    <Image
                        data-testid="captchaImage"
                        id="captchaImage"
                        block
                        src={`data:image/jpeg;base64, ${getCaptchaResponse.content}`}
                        alt="Hip image" /> :
                    getCaptchaResponse && !isLoading &&
                    <audio
                        data-testid="captchaAudio"
                        id="captchaAudio"
                        src={`data:audio/mp3;base64, ${getCaptchaResponse.content}`}
                        controls />
                }
            </div>
            <Input
                className="captcha-input"
                aria-label={challengeTypeLabel}
                data-testid="captchaSolutionInput"
                placeholder={challengeTypeLabel}
                value={inputSolution}
                onKeyDown={(event) => { if (event.key === 'Enter') { handleVerification() } }}
                onChange={(_, data) => setInputSolution(data.value)} />
            <Button
                data-testid="captchaVerificationButton"
                id="captchaVerificationButton"
                appearance="primary"
                disabled={isVerifying}
                onClick={handleVerification}>
                {t("HIP.verifyCaptchaButton")}
            </Button>
            {(isLoading || isVerifying) &&
                <div className="loader-container">
                    <Spinner size="huge" className="loader" />
                </div>
            }
        </div>
    )
}

export function CaptchaControl({ onVerification, onCaptchaLoad, baseUrl }: CaptchaControlProps) {
    return (<I18nextProvider i18n={i18next}>
        <CaptchaControlInternal onVerification={onVerification} onCaptchaLoad={onCaptchaLoad} baseUrl={baseUrl} />
    </I18nextProvider>);
}